<template>
    <Upload
        :action="uploadAction"
        :data="formData"
        :show-upload-list="false"
        :show-progress="true"
        :on-success="onSuccess"
        :on-progress="onProgress"
        :multiple="true"
    >
        <Button type="primary" :data-testid="dataTestid" @click="updatePostData">{{ label }}</Button>
    </Upload>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapStores } from "pinia";

import { TemplateType } from "hoxton-shared/enums/masterTemplate";
import { USER_ACTIONS } from "hoxton-shared/enums/user";

import type { MasterTemplateFile, InputAEComposition } from "@/types/types";
import clientMasterTemplatesUploadConfigQuery from "@/apollo/queries/v2/ClientMasterTemplatesUploadConfig.gql";
import bus from "@/bus";

import MasterTemplates from "@/services/MasterTemplates";
import { uploadConfigExpired } from "@/utils";
import { createAEProject } from "@/utils/ae";
import { pollInDesignUpload } from "@/utils/CreativeIntelligenceApi/indesign";

import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";

import { MasterTemplateEvents, MasterTemplateUploadStatus } from "@/enums/masterTemplate";
import { useMasterTemplatesLibraryStore } from "@/store/pinia/masterTemplatesLibrary";
import { userCan } from "@/services/Auth";

export default defineComponent({
    name: "MasterTemplateUploadButton",

    props: {
        label: {
            type: String,
            required: true
        },
        dataTestid: {
            type: String,
            required: false,
            default: ""
        }
    },

    setup() {
        const ga4 = useGA4Tracking();

        return {
            ga4
        };
    },

    data() {
        return {
            isLoading: false,
            formData: {},
            masterTemplateService: null as null | MasterTemplates
        };
    },

    computed: {
        ...mapStores(useMasterTemplatesLibraryStore),
        userCanManageMasterTemplates() {
            return userCan(USER_ACTIONS.canManageMasterTemplates, { clientId: this.clientId }, false);
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        persistentBucket() {
            if (!this.uploadConfig) {
                return "";
            }

            // Compute the persistent bucket name in this way for simplicity, based on the temporary bucket name
            // so we avoid the complexity of fetching it from the BE, at least for now. It's harmless and the chances
            // to break something in the feature are linked to the probability to rename the buckets, so extremely low.
            return this.uploadConfig?.action
                ?.split("//")[1]
                ?.split(".s3-eu-west-1.amazonaws.com")[0]
                ?.replace("temporary", "persistent");
        },

        uploadAction() {
            return this.uploadConfig?.action || "";
        },

        uploadConfig() {
            return this.uploadConfigObject?.config;
        },

        uploadConfigObject() {
            return this.masterTemplatesLibraryStore.uploadConfig;
        }
    },

    created() {
        /*
        Trigger the refetching of the upload config in case it has expired. The skip() check
        in the query definition will stop it from being executed if its not needed.
        */
        this.$apollo.queries.masterTemplateUploadConfig?.refetch();

        // @ts-expect-error known issue
        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);
    },

    methods: {
        findDuplicateLayerNames(compositions: InputAEComposition[]) {
            return compositions.reduce((acc, composition) => {
                const layerMap = new Map<string, Set<string>>();

                composition.layers.forEach(layer => {
                    if (!layerMap.has(layer.name)) {
                        layerMap.set(layer.name, new Set([layer.id]));
                    } else {
                        const idSet = layerMap.get(layer.name)!;
                        if (!idSet.has(layer.id)) {
                            acc.push(layer.name);
                        }
                        idSet.add(layer.id);
                    }
                });

                return acc;
            }, [] as string[]);
        },

        async createPlainlyProject(masterTemplateId: string, key: string, file: MasterTemplateFile) {
            try {
                const { plainlyProjectId, compositions } = await createAEProject({
                    clientId: this.clientId,
                    persistentBucket: this.persistentBucket!,
                    key
                });

                const duplicates = this.findDuplicateLayerNames(compositions);

                if (duplicates.length) {
                    throw new Error(`Duplicate layer names found: ${duplicates.join(", ")}`);
                }

                await this.masterTemplateService?.completeAE(masterTemplateId, plainlyProjectId, compositions);

                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.UPLOADED,
                    adType: TemplateType.AE
                });

                bus.$emit(MasterTemplateEvents.RefreshMasterTemplateList);
            } catch (e) {
                await this.masterTemplateService?.delete(masterTemplateId);

                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.FAILED,
                    adType: TemplateType.AE,
                    errorMessage: "Failed to create plainly project: " + (e as Error).message
                });
            }
        },

        async pollInDesignUpload(resourceId: string, file: MasterTemplateFile) {
            await pollInDesignUpload(resourceId);

            bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                ...file,
                status: MasterTemplateUploadStatus.UPLOADED,
                adType: TemplateType.INDESIGN
            });

            bus.$emit(MasterTemplateEvents.RefreshMasterTemplateList);
        },

        async createTemplate(zipUrl: string, file: MasterTemplateFile) {
            try {
                if (!this.masterTemplateService) {
                    throw new Error("MasterTemplateService not initialized.");
                }

                const response = await this.masterTemplateService.add(zipUrl);
                const addMasterTemplatePayload = response.data.addMasterTemplate;
                let masterTemplate;
                let adType;

                if (addMasterTemplatePayload.previewMode === "RESOURCE_MANAGER") {
                    // Check if budget limit was hit
                    if (addMasterTemplatePayload.resource?.status === "BUDGET_LIMIT_HIT") {
                        throw new Error("Your InDesign usage limit has been reached.");
                    }

                    if (!addMasterTemplatePayload.resource?.resourceId) {
                        throw new Error("Missing resource details for async template upload");
                    }

                    await this.pollInDesignUpload(addMasterTemplatePayload.resource.resourceId, file);
                } else {
                    masterTemplate = addMasterTemplatePayload.masterTemplate;
                    adType = masterTemplate?.adType;

                    if (!masterTemplate) {
                        throw new Error("No master template returned from addMasterTemplate mutation");
                    }

                    if (!adType) {
                        throw new Error("No ad type returned from addMasterTemplate mutation");
                    }

                    if (masterTemplate.adType === TemplateType.AE) {
                        this.createPlainlyProject(masterTemplate._id, masterTemplate.persistentKey, file);
                    } else {
                        bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                            ...file,
                            status: MasterTemplateUploadStatus.UPLOADED,
                            adType: masterTemplate.adType
                        });

                        bus.$emit(MasterTemplateEvents.RefreshMasterTemplateList);
                    }
                }

                const freeformValues = [];

                if (masterTemplate) {
                    freeformValues.push(masterTemplate.adType);
                    freeformValues.push(masterTemplate._id);
                } else if (addMasterTemplatePayload?.resource?.resourceId) {
                    freeformValues.push(TemplateType.INDESIGN);
                    freeformValues.push(addMasterTemplatePayload.resource.resourceId);
                }

                freeformValues.push("Template uploaded successfully");

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES,
                    featureContext: GA4.FEATURE_CONTEXTS.ADD_TEMPLATE,
                    freeformValues
                });
            } catch (e) {
                this.ga4.trackError({
                    errorMessage: (e as Error).message,
                    errorType: GA4.ERROR_TYPES.VALIDATION_ERROR,
                    origin: GA4.ERROR_ORIGINS.BACKEND,
                    featureName: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES
                });

                bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                    ...file,
                    status: MasterTemplateUploadStatus.FAILED,
                    errorMessage: (e as Error).message
                });
            }
        },

        onSuccess(_: unknown, file: MasterTemplateFile) {
            const zipUrl = `${this.uploadConfig?.filePrefix}/${file.name}`;

            this.createTemplate(zipUrl, file);
        },

        onProgress(_: unknown, file: MasterTemplateFile) {
            bus.$emit(MasterTemplateEvents.UpdateMasterTemplateUploadList, {
                ...file,
                status: MasterTemplateUploadStatus.UPLOADING
            });
        },

        updatePostData() {
            this.ga4.trackFeatureEvent({
                featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
                featureCategory: GA4.FEATURE_CATEGORIES.MASTER_TEMPLATES,
                featureContext: GA4.FEATURE_CONTEXTS.ADD_TEMPLATE
            });
            // Required to manually add in the Key for some reason.
            if (!this.uploadConfig || !this.uploadConfig.postData) {
                return;
            }

            const data = JSON.parse(this.uploadConfig.postData);
            data.key = `${this.uploadConfig.filePrefix}/\${filename}`;
            this.formData = data;
        }
    },

    apollo: {
        masterTemplateUploadConfig: {
            query: clientMasterTemplatesUploadConfigQuery,
            variables() {
                return {
                    clientId: this.clientId
                };
            },
            skip() {
                if (!this.userCanManageMasterTemplates || !this.clientId) {
                    return true;
                }

                if (this.uploadConfigObject?.clientId === this.clientId) {
                    // Check if the one we have is expired or not
                    if (this.uploadConfig && uploadConfigExpired(this.uploadConfig)) {
                        return false;
                    }

                    this.isLoading = false;

                    return true;
                }

                return false;
            },
            manual: true,
            result({ data }) {
                this.masterTemplatesLibraryStore.uploadConfig = {
                    clientId: this.clientId,
                    config: data.masterTemplateUploadConfig
                };

                this.isLoading = false;
            },
            error(e: Error) {
                bus.$emit("apolloErrorEvent", e);
            }
        }
    }
});
</script>
